import type { SourceWindow, ViewRect } from "../shared/geometry";
import type { PixelSource } from "./camera-frame";

export const PAD_VALUE = 114;

/** Ultralytics' rounded resize and asymmetric padding. */
export function letterboxGeometry(width: number, height: number, area: ViewRect) {
  const scale = Math.min(width / area.w, height / area.h);
  const scaledWidth = Math.max(1, Math.round(area.w * scale));
  const scaledHeight = Math.max(1, Math.round(area.h * scale));
  return {
    scale,
    scaledWidth,
    scaledHeight,
    left: Math.round((width - scaledWidth) / 2 - 0.1),
    top: Math.round((height - scaledHeight) / 2 - 0.1),
  };
}

/** Flat window → canvas transform. Projective windows need a GPU sampler. */
export function windowTransform(
  window: SourceWindow,
  dx: number,
  dy: number,
  dw: number,
  dh: number,
  area?: ViewRect,
): [number, number, number, number, number, number] {
  const [a, b, tx, c, d, ty] = window.affine;
  const sx = area?.x ?? 0,
    sy = area?.y ?? 0;
  const kx = dw / (area?.w ?? window.sw),
    ky = dh / (area?.h ?? window.sh);
  return [
    kx * a,
    ky * b,
    kx * c,
    ky * d,
    dx - kx * (a * tx + c * ty + sx),
    dy - ky * (b * tx + d * ty + sy),
  ];
}

/** Reusable model-size canvas; callers choose GPU copying or CPU readback. */
export function letterboxSurface(cpu = false) {
  let canvas: HTMLCanvasElement | null = null;
  let context: CanvasRenderingContext2D | null = null;
  return {
    draw(source: PixelSource, window: SourceWindow, width: number, height: number, area: ViewRect) {
      if (!window.sw || !window.sh || !area.w || !area.h || window.keystone) return null;
      if (!canvas) canvas = document.createElement("canvas");
      if (canvas.width !== width || canvas.height !== height || !context) {
        canvas.width = width;
        canvas.height = height;
        context = canvas.getContext("2d", { willReadFrequently: cpu });
        if (context) context.imageSmoothingQuality = "high";
      }
      if (!context) return null;
      const geometry = letterboxGeometry(width, height, area);
      context.fillStyle = `rgb(${PAD_VALUE},${PAD_VALUE},${PAD_VALUE})`;
      context.fillRect(0, 0, width, height);
      context.save();
      context.beginPath();
      context.rect(geometry.left, geometry.top, geometry.scaledWidth, geometry.scaledHeight);
      context.clip();
      context.setTransform(
        ...windowTransform(
          window,
          geometry.left,
          geometry.top,
          geometry.scaledWidth,
          geometry.scaledHeight,
          area,
        ),
      );
      context.drawImage(source, 0, 0);
      context.restore();
      return { canvas, context, ...geometry };
    },
    release() {
      if (canvas) canvas.width = canvas.height = 0;
      canvas = null;
      context = null;
    },
  };
}
